const DISCLAIMER = "Ce dataset agrégé ne permet pas de conclure sur la croissance ou la saisonnalité.";

const numberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const percent = (value) => `${new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}).format(value)} %`;

const usd = (value) => `${new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 0,
}).format(value)} $`;

const passengerShare = (item) => numberOrNull(item?.passenger_share ?? item?.share);
const revenueShare = (item) => numberOrNull(item?.revenue_share ?? item?.share);

const usableRows = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => row && row.name && numberOrNull(row.raw) !== null);

/**
 * Produce descriptive aviation insights from the Lodge-calculated passenger
 * and revenue rankings. Provider-specific ADI share is deliberately ignored.
 */
export function deriveAirInsights(mi = {}) {
  const passengerRows = usableRows(mi.topAirlinesByPassengers);
  const revenueRows = usableRows(mi.topAirlinesByRevenue);
  const totalPassengers = numberOrNull(mi.kpis?.airPassengers?.raw);
  const totalRevenue = numberOrNull(mi.kpis?.airRevenue?.raw);
  const averageFare = numberOrNull(mi.kpis?.averageFare?.raw)
    ?? (totalPassengers > 0 && totalRevenue !== null ? totalRevenue / totalPassengers : null);

  const airlines = new Map();
  passengerRows.forEach((row) => {
    airlines.set(row.name, {
      name: row.name,
      passengerRaw: numberOrNull(row.raw),
      passengerShare: passengerShare(row),
      revenueRaw: null,
      revenueShare: numberOrNull(row.revenue_share),
    });
  });
  revenueRows.forEach((row) => {
    const current = airlines.get(row.name) || {
      name: row.name,
      passengerRaw: null,
      passengerShare: numberOrNull(row.passenger_share),
    };
    airlines.set(row.name, {
      ...current,
      revenueRaw: numberOrNull(row.raw),
      revenueShare: revenueShare(row),
      passengerShare: current.passengerShare ?? numberOrNull(row.passenger_share),
    });
  });

  const enriched = [...airlines.values()].map((airline) => ({
    ...airline,
    implicitFare: airline.passengerRaw > 0 && airline.revenueRaw !== null
      ? airline.revenueRaw / airline.passengerRaw
      : null,
  }));
  const insights = [];

  const shares = passengerRows.map(passengerShare);
  if (shares.length >= 3 && shares.slice(0, 3).every((value) => value !== null)) {
    const shareForTop = (count) => totalPassengers > 0
      ? passengerRows.slice(0, count).reduce((sum, row) => sum + Number(row.raw), 0) / totalPassengers * 100
      : shares.slice(0, count).reduce((sum, value) => sum + value, 0);
    const top3 = shareForTop(3);
    const top5 = shares.length >= 5 && shares.slice(0, 5).every((value) => value !== null)
      ? shareForTop(5)
      : null;
    insights.push({
      id: "concentration",
      eyebrow: "Structure du marché",
      title: "Concentration du marché",
      statement: top5 !== null
        ? `Le trafic passagers est concentré : les 3 premières compagnies réunissent ${percent(top3)} des passagers, et les 5 premières ${percent(top5)}.`
        : `Les 3 premières compagnies réunissent ${percent(top3)} des passagers.`,
      evidence: [
        { label: "Top 3 compagnies", value: percent(top3) },
        ...(top5 !== null ? [{ label: "Top 5 compagnies", value: percent(top5) }] : []),
      ],
      implication: "Un noyau restreint de transporteurs structure l’accès au marché : à prioriser dans toute négociation de connectivité.",
    });
  }

  const leaderRow = passengerRows[0];
  const leader = leaderRow ? enriched.find((row) => row.name === leaderRow.name) : null;
  if (leader?.passengerShare !== null) {
    const revenuePhrase = leader.revenueShare !== null ? ` et ${percent(leader.revenueShare)} des revenus` : "";
    insights.push({
      id: "leader",
      eyebrow: "Acteur structurant",
      title: "Compagnie leader",
      statement: `${leader.name} domine le marché avec ${percent(leader.passengerShare)} des passagers${revenuePhrase}.`,
      evidence: [
        { label: "Part passagers", value: percent(leader.passengerShare) },
        ...(leader.revenueShare !== null ? [{ label: "Part revenus", value: percent(leader.revenueShare) }] : []),
        ...(leader.implicitFare !== null ? [{ label: "Tarif implicite", value: usd(leader.implicitFare) }] : []),
      ],
      implication: "Partenaire structurant à prioriser pour toute stratégie d’accès au marché.",
    });
  }

  const comparable = enriched.filter((row) => row.passengerShare !== null && row.revenueShare !== null);
  const highValue = comparable
    .filter((row) => row.revenueShare > row.passengerShare)
    .sort((a, b) => (b.revenueShare - b.passengerShare) - (a.revenueShare - a.passengerShare))[0];
  if (highValue) {
    const farePhrase = highValue.implicitFare !== null && averageFare !== null
      ? `, avec un tarif implicite de ${usd(highValue.implicitFare)} contre ${usd(averageFare)} en moyenne`
      : "";
    insights.push({
      id: "high-value",
      eyebrow: "Valeur unitaire",
      title: "Compagnie à forte valeur",
      statement: `${highValue.name} pèse davantage en revenus (${percent(highValue.revenueShare)}) qu’en passagers (${percent(highValue.passengerShare)})${farePhrase}.`,
      evidence: [
        { label: "Part passagers", value: percent(highValue.passengerShare) },
        { label: "Part revenus", value: percent(highValue.revenueShare) },
        ...(highValue.implicitFare !== null ? [{ label: "Tarif implicite", value: usd(highValue.implicitFare) }] : []),
        ...(averageFare !== null ? [{ label: "Tarif moyen marché", value: usd(averageFare) }] : []),
      ],
      implication: "Clientèle à plus forte contribution unitaire — segment premium ou affaires à étudier.",
    });
  }

  const volumeLed = comparable
    .filter((row) => row.passengerShare >= 1 && row.passengerShare > row.revenueShare)
    .sort((a, b) => (b.passengerShare - b.revenueShare) - (a.passengerShare - a.revenueShare))[0];
  if (volumeLed) {
    insights.push({
      id: "volume",
      eyebrow: "Levier de volume",
      title: "Compagnie orientée volume",
      statement: `${volumeLed.name} génère un fort volume passagers (${percent(volumeLed.passengerShare)}) pour une part de revenus plus faible (${percent(volumeLed.revenueShare)}).`,
      evidence: [
        { label: "Part passagers", value: percent(volumeLed.passengerShare) },
        { label: "Part revenus", value: percent(volumeLed.revenueShare) },
        ...(volumeLed.implicitFare !== null ? [{ label: "Tarif implicite", value: usd(volumeLed.implicitFare) }] : []),
      ],
      implication: "Levier potentiel pour capter du volume plutôt que de la valeur unitaire.",
    });
  }

  return {
    ok: insights.length >= 2,
    insights: insights.slice(0, 4),
    disclaimer: DISCLAIMER,
  };
}

export { DISCLAIMER as AIR_INSIGHTS_DISCLAIMER };
